import React from 'react';
import { FaCog, FaStickyNote, FaUsers, FaMapMarkerAlt, FaCalendarDay, FaClock, FaUserCheck } from 'react-icons/fa';
import { colors, shadows } from '../features/employees/styles/personalakteStyles';
import dateUtil from '../utils/dateUtil';

interface ShiftDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  shift: any; // Verwende any um Type-Konflikte zu vermeiden
}

function formatDateTime(isoString: string): { date: string; time: string; weekday: string } {
  if (!isoString) return { date: '--', time: '--:--', weekday: '--' };

  const dt = dateUtil.convertUTCToTenantTime(isoString) || new Date(isoString);
  const weekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  return {
    date: dt.toLocaleDateString('de-DE'),
    time: `${dateUtil.pad2(dt.getHours())}:${dateUtil.pad2(dt.getMinutes())}`,
    weekday: weekdays[dt.getDay()]
  };
}

export default function ShiftDetailsModal({
  isOpen,
  onClose,
  onEdit,
  shift
}: ShiftDetailsModalProps): React.ReactElement | null {
  if (!isOpen || !shift) return null;

  const startInfo = formatDateTime(shift.start_time);
  const endInfo = formatDateTime(shift.end_time);
  const assigned = shift.assigned_employees || [];
  const applications = shift.applications?.filter(app => app.status === 'APPLIED') || [];
  const notes = shift.notes || shift.catering_notes || '';

  const handleEditClick = (): void => {
    onClose();
    onEdit();
  };

  return (
    <div
      data-testid="shift-details-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div
        data-testid="shift-details-modal-content"
        style={{
          backgroundColor: colors.bgCard,
          padding: '24px',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: shadows.elevated
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header mit Schließen-Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: colors.navy, fontSize: '1.4em', fontWeight: '600' }}>
            Schicht-Details
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: colors.textMuted,
              padding: '0 5px',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>

        {/* Zeile 1: Tag & Datum */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <FaCalendarDay style={{ marginRight: '8px', color: colors.navy, fontSize: '16px' }} />
            <span style={{ fontWeight: '600', fontSize: '1.1em', color: colors.textBody }}>
              {startInfo.weekday}, {startInfo.date}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
            <FaClock style={{ marginRight: '8px', color: colors.navy, fontSize: '14px' }} />
            <span style={{ fontSize: '1em', color: colors.textMuted }}>
              {startInfo.time} - {endInfo.time}
            </span>
          </div>
        </div>

        {/* Zeile 2: Work Area */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FaMapMarkerAlt style={{ marginRight: '8px', color: colors.navy, fontSize: '16px' }} />
            <span style={{ fontWeight: '600', fontSize: '1.1em', color: colors.textBody }}>
              Work Area
            </span>
          </div>
          <div 
            style={{ 
              marginLeft: '24px', 
              marginTop: '4px',
              display: 'inline-block',
              backgroundColor: shift.work_area__color || '#f0f0f0',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '6px',
              fontSize: '0.9em',
              fontWeight: '500'
            }}
          >
            {shift.work_area__name || 'Unbekannt'}
          </div>
        </div>

        {/* Zeile 3: Zugeteilte Mitarbeiter */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <FaUsers style={{ marginRight: '8px', color: colors.navy, fontSize: '16px' }} />
            <span style={{ fontWeight: '600', fontSize: '1.1em', color: colors.textBody }}>
              Zugeteilte Mitarbeiter ({assigned.length}/ {shift.employees_needed || 1})
            </span>
          </div>
          <div style={{ marginLeft: '24px' }}>
            {assigned.length === 0 ? (
              <span style={{ color: colors.textLight, fontStyle: 'italic' }}>Keine Mitarbeiter zugewiesen</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {assigned.map(emp => (
                  <div key={emp.id} style={{ 
                    padding: '6px 10px', 
                    fontSize: '0.9em'
                  }}>
                    <FaUserCheck style={{ marginRight: '6px', color: colors.navy, fontSize: '12px' }} />
                    {emp.first_name} {emp.last_name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zeile 4: Notes */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <FaStickyNote style={{ marginRight: '8px', color: colors.navy, fontSize: '16px' }} />
            <span style={{ fontWeight: '600', fontSize: '1.1em', color: colors.textBody }}>
              Notizen
            </span>
          </div>
          <div style={{ marginLeft: '24px' }}>
            {notes ? (
              <div style={{ 
                fontSize: '0.9em',
                lineHeight: '1.4',
                whiteSpace: 'pre-line',
                color: colors.textBody,
                padding: '8px 0'
              }}>
                {notes}
              </div>
            ) : (
              <span style={{ color: colors.textLight, fontStyle: 'italic' }}>Keine Notizen</span>
            )}
          </div>
        </div>

        {/* Zeile 5: Bewerbungen */}
        {applications.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
               <div style={{ 
                 width: '14px', 
                 height: '14px', 
                 borderRadius: '50%', 
                 backgroundColor: colors.navy,
                 marginRight: '8px'
               }} />
               <span style={{ fontWeight: '600', fontSize: '1.1em', color: colors.textBody }}>
                 Bewerbungen ({applications.length})
               </span>
             </div>
             <div style={{ marginLeft: '24px' }}>
               <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                 {applications.map(app => (
                   <div key={app.id} style={{ 
                     padding: '6px 10px', 
                     fontSize: '0.9em'
                   }}>
                     {app.employee_name || `Mitarbeiter #${app.employee}`}
                     <span style={{ marginLeft: '8px', color: colors.textMuted, fontSize: '0.8em' }}>
                       ({app.type})
                     </span>
                   </div>
                 ))}
               </div>
             </div>
          </div>
        )}

        {/* Edit Button */}
        <div style={{ textAlign: 'right', borderTop: `1px solid ${colors.borderLight}`, paddingTop: '16px' }}>
                       <button
               onClick={handleEditClick}
               style={{
                 backgroundColor: colors.navy,
                 color: 'white',
                 border: 'none',
                 borderRadius: '10px',
                 padding: '10px 20px',
                 fontSize: '0.9em',
                 fontWeight: '500',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 marginLeft: 'auto',
                 transition: 'background-color 0.2s'
               }}
               onMouseOver={(e) => e.currentTarget.style.backgroundColor = colors.slateDark}
               onMouseOut={(e) => e.currentTarget.style.backgroundColor = colors.navy}
               data-testid="edit-shift-button"
             >
               <FaCog style={{ fontSize: '14px' }} />
               Schicht bearbeiten
             </button>
        </div>
      </div>
    </div>
  );
} 